import React from "react";
import SelectModel, { Option } from "./SelectModel";

let UNIQUE_ID = 0;

export interface SelectComponentProps {
    select: HTMLSelectElement;
    container: HTMLDivElement;
}

interface SelectComponentState {
    change: number;
    open: boolean;
    visuallyHighlighted: number | null;
}

export default class SelectComponent extends React.Component<
    SelectComponentProps,
    SelectComponentState
> {
    private readonly model: SelectModel;
    private readonly id: string = `lf-select-component--${UNIQUE_ID++}`;
    private readonly listboxRef: React.RefObject<HTMLUListElement>;
    private readonly container: HTMLDivElement;
    private readonly labelId;

    constructor(props: SelectComponentProps) {
        super(props);
        this.container = props.container;
        this.labelId = getLabelId(props.select, this.id);
        this.model = new SelectModel(props.select);
        this.state = {
            change: 0,
            open: false,
            visuallyHighlighted: null,
        };
        this.listboxRef = React.createRef();
    }

    get buttonId(): string {
        return this.id + "--button";
    }

    componentDidMount() {
        document.addEventListener("click", this.handleDocumentClick);
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleDocumentClick);
    }

    componentDidUpdate(
        prevProps: SelectComponentProps,
        prevState: SelectComponentState
    ) {
        if (prevState.open !== this.state.open && this.state.open) {
            this.listboxRef.current!.focus();
        }
    }

    render() {
        const options = this.model.getOptions();
        const selected = this.model.getSelectedOptions();

        const selectedText = selected.map(getText).join(", ");

        const isOpenClass = this.state.open ? "is-open" : "";
        const menuClass = `lf-select__menu ${isOpenClass}`;

        return (
            <>
                <button
                    type="button"
                    className="lf-select__button"
                    aria-haspopup="listbox"
                    aria-expanded={this.state.open}
                    id={this.buttonId}
                    aria-labelledby={`${this.labelId} ${this.buttonId}`}
                    onClick={this.handleOpen}
                >
                    {selectedText}
                    <span className="icon">
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M3.37658 7.36612C3.87868 6.87796 4.69275 6.87796 5.19485 7.36612L12 13.9822L18.8051 7.36612C19.3073 6.87796 20.1213 6.87796 20.6234 7.36612C21.1255 7.85427 21.1255 8.64573 20.6234 9.13388L12.9091 16.6339C12.407 17.122 11.593 17.122 11.0909 16.6339L3.37658 9.13388C2.87447 8.64573 2.87447 7.85427 3.37658 7.36612Z"
                                fill="#FF3D6D"
                            />
                        </svg>
                    </span>
                </button>
                <ul
                    ref={this.listboxRef}
                    className={menuClass}
                    role="listbox"
                    aria-multiselectable={this.model.multiple}
                    aria-activedescendant={this.getOptionId(
                        this.state.visuallyHighlighted
                    )}
                    onKeyDown={this.handleKeyboard}
                    onBlur={this.handleClose}
                    tabIndex={-1}
                >
                    {options.map((o, index) => (
                        <OptionComponent
                            key={o.value + index}
                            id={this.getOptionId(index)}
                            value={o.value}
                            index={index}
                            isSelected={this.model.isSelected(o.value)}
                            isVisuallyHighlighted={
                                index === this.state.visuallyHighlighted
                            }
                            onSelect={this.handleSelect}
                            onVisuallyHighlight={this.highlightVisually}
                        >
                            {o.text}
                        </OptionComponent>
                    ))}
                </ul>
            </>
        );
    }

    handleSelect = (value: string, index: number) => {
        this.model.select(value);
        this.setState((state) => {
            return {
                change: state.change + 1,
                visuallyHighlighted: index,
                open: this.model.multiple,
            };
        });
    };

    handleOpen = () => {
        this.setState({ open: true, visuallyHighlighted: 0 });
    };

    handleClose = () => {
        this.setState({ open: false, visuallyHighlighted: null });
    };

    handleDocumentClick = (event: MouseEvent): void => {
        if (!this.container) return;
        if (!this.container.contains(event.target as Node)) {
            this.setState({ open: false });
        }
    };

    handleKeyboard = (e: React.KeyboardEvent): void => {
        switch (e.key) {
            case "Escape":
                this.handleClose();
                break;
            case "Enter":
                const option = this.model.getOptions()[
                    this.state.visuallyHighlighted!
                ];
                this.handleSelect(
                    option.value,
                    this.state.visuallyHighlighted!
                );
                break;
            case "Home":
                this.setState({ visuallyHighlighted: 0 });
                break;
            case "End":
                this.setState({ visuallyHighlighted: this.model.length - 1 });
                break;
            case "ArrowDown":
                this.setState((state) => ({
                    visuallyHighlighted: this.optionIndexBoundaries(
                        state.visuallyHighlighted! + 1
                    ),
                }));
                e.preventDefault();
                break;
            case "ArrowUp":
                this.setState((state) => ({
                    visuallyHighlighted: this.optionIndexBoundaries(
                        state.visuallyHighlighted! - 1
                    ),
                }));
                e.preventDefault();
                break;
        }
    };

    highlightVisually = (index: number): void => {
        this.setState({ visuallyHighlighted: index });
    };

    getOptionId(value: null): undefined;
    getOptionId(value: number): string;
    getOptionId(value: number | null): string | undefined;
    getOptionId(value: number | null): string | undefined {
        if (value === null) return undefined;
        return this.id + "--option--" + value;
    }

    optionIndexBoundaries(value: number): number {
        return (this.model.length + value) % this.model.length;
    }
}

interface OptionProps {
    id: string;
    value: string;
    index: number;
    isSelected: boolean;
    isVisuallyHighlighted: boolean;
    onSelect: (value: string, index: number) => void;
    onVisuallyHighlight: (index: number) => void;
    children: React.ReactNode;
}

function OptionComponent({
    id,
    value,
    children,
    index,
    isSelected,
    isVisuallyHighlighted,
    onVisuallyHighlight,
    onSelect,
}: OptionProps): JSX.Element {
    const handleClick = React.useCallback(() => {
        onSelect(value, index);
    }, [value, index]);
    const handleMouseOver = React.useCallback(() => {
        onVisuallyHighlight(index);
    }, [index]);

    const isSelectedClass = isSelected ? "is-selected" : "";
    const isHighlightedClass = isVisuallyHighlighted ? "is-highlighted" : "";
    const classes = [
        "lf-select__option",
        isSelectedClass,
        isHighlightedClass,
    ].join(" ");

    return (
        <li
            id={id}
            role="option"
            className={classes}
            onClick={handleClick}
            onMouseOver={handleMouseOver}
        >
            {children}
        </li>
    );
}

function getText(o: Option): string {
    return o.text;
}

function getLabel(select: HTMLSelectElement): HTMLLabelElement | null {
    let current: HTMLElement | null = select;
    while (current) {
        if (current.tagName === "LABEL") return current as HTMLLabelElement;
        current = current.parentElement;
    }

    if (select.hasAttribute("id")) {
        return document.querySelector(
            `label[for=${select.getAttribute("id")!}]`
        );
    }

    return null;
}

function getLabelId(select: HTMLSelectElement, selectId: string): string {
    const label: HTMLLabelElement | null = getLabel(select);
    const fallbackId = selectId + "--label";

    if (label && !label.hasAttribute("id")) {
        label.setAttribute("id", fallbackId);
    }

    return label?.getAttribute("id") ?? "";
}
